import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export const useWorkoutStore = create(
  persist(
    (set, get) => ({
      // State
      workouts: [
        // Sample workouts for demo
        {
          id: 'demo-workout-1',
          name: 'Morning Cardio',
          type: 'cardio',
          duration: 30,
          date: new Date().toISOString().split('T')[0],
          exercises: [
            { id: '4', name: 'Burpees', sets: 3, reps: 10, duration: null },
            { id: '8', name: 'Mountain Climbers', sets: 3, reps: 20, duration: null }
          ],
          status: 'completed',
          completedAt: new Date().toISOString(),
          createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString() // 2 hours ago
        },
        {
          id: 'demo-workout-2',
          name: 'Upper Body Strength',
          type: 'strength',
          duration: 45,
          date: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Yesterday
          exercises: [
            { id: '1', name: 'Push-ups', sets: 3, reps: 15, duration: null },
            { id: '6', name: 'Bench Press', sets: 4, reps: 8, duration: null },
            { id: '7', name: 'Pull-ups', sets: 3, reps: 6, duration: null }
          ],
          status: 'completed',
          completedAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          createdAt: new Date(Date.now() - 25 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 'demo-workout-3',
          name: 'Full Body HIIT',
          type: 'hiit',
          duration: 25,
          date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 2 days ago
          exercises: [
            { id: '2', name: 'Squats', sets: 4, reps: 12, duration: null },
            { id: '4', name: 'Burpees', sets: 3, reps: 8, duration: null },
            { id: '3', name: 'Plank', sets: 3, reps: null, duration: 60 }
          ],
          status: 'completed',
          completedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000 - 60 * 60 * 1000).toISOString()
        }
      ],
      currentWorkout: null,
      workoutPlans: [],
      exercises: [
        // Sample exercises database
        { id: '1', name: 'Push-ups', category: 'chest', equipment: 'bodyweight', difficulty: 'beginner' },
        { id: '2', name: 'Squats', category: 'legs', equipment: 'bodyweight', difficulty: 'beginner' },
        { id: '3', name: 'Plank', category: 'core', equipment: 'bodyweight', difficulty: 'beginner' },
        { id: '4', name: 'Burpees', category: 'cardio', equipment: 'bodyweight', difficulty: 'intermediate' },
        { id: '5', name: 'Deadlifts', category: 'back', equipment: 'barbell', difficulty: 'intermediate' },
        { id: '6', name: 'Bench Press', category: 'chest', equipment: 'barbell', difficulty: 'intermediate' },
        { id: '7', name: 'Pull-ups', category: 'back', equipment: 'pull-up bar', difficulty: 'advanced' },
        { id: '8', name: 'Mountain Climbers', category: 'cardio', equipment: 'bodyweight', difficulty: 'intermediate' },
      ],
      isLoading: false,
      error: null,

      // Actions
      addWorkout: (workout) => {
        const newWorkout = {
          id: Date.now().toString(),
          ...workout,
          createdAt: new Date().toISOString(),
          completedAt: null,
          status: 'planned'
        }
        
        set(state => ({
          workouts: [...state.workouts, newWorkout]
        }))
      },

      updateWorkout: (id, updates) => {
        set(state => ({
          workouts: state.workouts.map(workout =>
            workout.id === id ? { ...workout, ...updates } : workout
          )
        }))
      },

      deleteWorkout: (id) => {
        set(state => ({
          workouts: state.workouts.filter(workout => workout.id !== id)
        }))
      },

      completeWorkout: (id, completionData) => {
        set(state => ({
          workouts: state.workouts.map(workout =>
            workout.id === id 
              ? { 
                  ...workout, 
                  status: 'completed',
                  completedAt: new Date().toISOString(),
                  actualDuration: completionData.duration,
                  actualExercises: completionData.exercises,
                  notes: completionData.notes
                }
              : workout
          )
        }))
      },

      startWorkout: (workout) => {
        set({ currentWorkout: { ...workout, startedAt: new Date().toISOString() } })
      },

      endWorkout: () => {
        set({ currentWorkout: null })
      },

      // Workout Plans
      createWorkoutPlan: (plan) => {
        const newPlan = {
          id: Date.now().toString(),
          ...plan,
          createdAt: new Date().toISOString(),
          isActive: false
        }
        
        set(state => ({
          workoutPlans: [...state.workoutPlans, newPlan]
        }))
      },

      updateWorkoutPlan: (id, updates) => {
        set(state => ({
          workoutPlans: state.workoutPlans.map(plan =>
            plan.id === id ? { ...plan, ...updates } : plan
          )
        }))
      },

      deleteWorkoutPlan: (id) => {
        set(state => ({
          workoutPlans: state.workoutPlans.filter(plan => plan.id !== id)
        }))
      },

      activateWorkoutPlan: (id) => {
        set(state => ({
          workoutPlans: state.workoutPlans.map(plan => ({
            ...plan,
            isActive: plan.id === id
          }))
        }))
      },

      // Exercise management
      addCustomExercise: (exercise) => {
        const newExercise = {
          id: Date.now().toString(),
          ...exercise,
          isCustom: true,
          createdAt: new Date().toISOString()
        }
        
        set(state => ({
          exercises: [...state.exercises, newExercise]
        }))
      },

      // Statistics
      getWorkoutStats: () => {
        const { workouts } = get()
        const completedWorkouts = workouts.filter(w => w.status === 'completed')
        
        return {
          totalWorkouts: completedWorkouts.length,
          totalDuration: completedWorkouts.reduce((sum, w) => sum + (w.actualDuration || 0), 0),
          averageDuration: completedWorkouts.length > 0 
            ? completedWorkouts.reduce((sum, w) => sum + (w.actualDuration || 0), 0) / completedWorkouts.length 
            : 0,
          workoutsThisWeek: completedWorkouts.filter(w => {
            const workoutDate = new Date(w.completedAt)
            const weekAgo = new Date()
            weekAgo.setDate(weekAgo.getDate() - 7)
            return workoutDate >= weekAgo
          }).length,
          favoriteCategory: this.getMostFrequentCategory(completedWorkouts)
        }
      },

      getMostFrequentCategory: (workouts) => {
        const categories = {}
        workouts.forEach(workout => {
          workout.exercises?.forEach(exercise => {
            const category = exercise.category || 'other'
            categories[category] = (categories[category] || 0) + 1
          })
        })
        
        return Object.keys(categories).reduce((a, b) => 
          categories[a] > categories[b] ? a : b, 'general'
        )
      },

      clearError: () => {
        set({ error: null })
      }
    }),
    {
      name: 'workout-storage',
      partialize: (state) => ({
        workouts: state.workouts,
        workoutPlans: state.workoutPlans,
        exercises: state.exercises
      })
    }
  )
)